/**
 * React hooks for secrets management
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { secretsApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { useAuthStore } from '@/store/auth';
import { buildRoleScopedQueryKey } from '@/lib/reactQueryRoleScope';
import type {
    CreateSecretRequest,
    UpdateSecretRequest,
    SecretMetadataResponse,
    CredentialUsagesResponse,
    DisconnectCredentialUsagesResponse,
    CredentialUsageReference,
    SecretIdentityReference,
    SecretOrganizationReference,
    SecretOrganizationUuidReference,
    SecretUuidReference,
} from '@anchorbase/pillar-sdk';

type SecretIdentityInput = (SecretIdentityReference | SecretUuidReference) | undefined;
type SecretMutationSecretReference = SecretIdentityReference | SecretUuidReference;
type SecretMutationOrganizationReference =
    | SecretOrganizationReference
    | SecretOrganizationUuidReference;

type SecretMutationReference = {
    secret: SecretMutationSecretReference;
    organization: SecretMutationOrganizationReference;
};
type SecretMutationOrganizationIdBridge = {
    secret: SecretMutationSecretReference;
    organizationId: number;
};
type LegacySecretMutationReference = {
    CredentialsId: number;
    organizationId: number;
};
type SecretMutationVariables =
    | SecretMutationReference
    | SecretMutationOrganizationIdBridge
    | LegacySecretMutationReference;

type DeleteSecretMutationVariables =
    | SecretMutationReference
    | SecretMutationOrganizationIdBridge;

function getSecretIdentityQueryKeyPart(secret: SecretIdentityInput): string | undefined {
    if (secret === undefined) {
        return undefined;
    }
    return secret.credential_uuid;
}

function getMutationSecretQueryKeyPart(variables: SecretMutationVariables): string | number {
    return 'secret' in variables
        ? variables.secret.credential_uuid
        : variables.CredentialsId;
}

function getMutationOrganizationQueryKeyPart(variables: SecretMutationVariables): string | number {
    return 'organization' in variables
        ? variables.organization.organization_uuid
        : variables.organizationId;
}

/**
 * Fetch a specific secret (with decrypted credentials)
 * Requires DISPLAY permission
 */
export function useSecret(secret: SecretIdentityInput) {
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    const secretQueryKeyPart: string | undefined = getSecretIdentityQueryKeyPart(secret);
    return useQuery({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'secrets',
            secretQueryKeyPart,
        ]),
        queryFn: async () => await secretsApi.getSecret(secret!),
        enabled: secret !== undefined,
    });
}

/**
 * Create a new secret
 */
export function useCreateSecret() {
    const queryClient = useQueryClient();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );

    return useMutation({
        mutationFn: (data: CreateSecretRequest) => secretsApi.createSecret(data),
        onSuccess: async () => {
            const listQueryKey = buildRoleScopedQueryKey(activeRoleAssignmentId, ['secrets']);
            await queryClient.invalidateQueries({
                queryKey: listQueryKey,
            });
            await queryClient.refetchQueries({
                queryKey: listQueryKey,
            });
        },
    });
}

/**
 * Update an existing secret
 */
export function useUpdateSecret(secret: SecretIdentityInput) {
    const log = createComponentLogger('useUpdateSecret');
    const queryClient = useQueryClient();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );

    return useMutation({
        mutationFn: (data: UpdateSecretRequest) => {
            if (secret === undefined) {
                throw new Error('Missing credential_uuid for secret update');
            }
            return secretsApi.updateSecret(secret, data);
        },
        onSuccess: (updatedSecret: SecretMetadataResponse) => {
            // Invalidate the specific secret
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'secrets',
                        getSecretIdentityQueryKeyPart(secret),
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries', err);
                }
            );
            // Invalidate secrets list for this organization
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'secrets',
                        'organization',
                        updatedSecret.organization_uuid,
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries', err);
                }
            );
        },
    });
}

/**
 * Delete a secret
 */
export function useDeleteSecret() {
    const log = createComponentLogger('useDeleteSecret');
    const queryClient = useQueryClient();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );

    return useMutation({
        mutationFn: (variables: DeleteSecretMutationVariables) =>
            secretsApi.deleteSecret(variables.secret),
        onSuccess: (_, variables) => {
            // Invalidate the specific secret
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'secrets',
                        getMutationSecretQueryKeyPart(variables),
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries', err);
                }
            );
            // Invalidate secrets list for this organization
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'secrets',
                        'organization',
                        getMutationOrganizationQueryKeyPart(variables),
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries', err);
                }
            );
        },
    });
}

/**
 * Disable a secret (is_active=false)
 */
export function useDisableSecret() {
    const log = createComponentLogger('useDisableSecret');
    const queryClient = useQueryClient();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );

    return useMutation({
        mutationFn: (variables: DeleteSecretMutationVariables) =>
            secretsApi.disableSecret(variables.secret),
        onSuccess: (_, variables) => {
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'secrets',
                        getMutationSecretQueryKeyPart(variables),
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'secrets',
                        'organization',
                        getMutationOrganizationQueryKeyPart(variables),
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries', err);
                }
            );
            runInBackground(
                queryClient.refetchQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'secrets',
                        'organization',
                        getMutationOrganizationQueryKeyPart(variables),
                    ]),
                }),
                (err) => {
                    log.error('Error refetching queries', err);
                }
            );
        },
    });
}

/**
 * Enable a secret (is_active=true)
 */
export function useEnableSecret() {
    const log = createComponentLogger('useEnableSecret');
    const queryClient = useQueryClient();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );

    return useMutation({
        mutationFn: (variables: DeleteSecretMutationVariables) =>
            secretsApi.enableSecret(variables.secret),
        onSuccess: (_, variables) => {
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'secrets',
                        getMutationSecretQueryKeyPart(variables),
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'secrets',
                        'organization',
                        getMutationOrganizationQueryKeyPart(variables),
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating queries', err);
                }
            );
            runInBackground(
                queryClient.refetchQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'secrets',
                        'organization',
                        getMutationOrganizationQueryKeyPart(variables),
                    ]),
                }),
                (err) => {
                    log.error('Error refetching queries', err);
                }
            );
        },
    });
}

export function useCredentialUsages(secret: SecretIdentityInput) {
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    const secretQueryKeyPart: string | undefined = getSecretIdentityQueryKeyPart(secret);
    return useQuery<CredentialUsagesResponse>({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'secrets',
            secretQueryKeyPart,
            'usages',
        ]),
        queryFn: async () => await secretsApi.listCredentialUsages(secret!),
        enabled: secret !== undefined,
    });
}

export function useDisconnectCredentialUsages() {
    const log = createComponentLogger('useDisconnectCredentialUsages');
    const queryClient = useQueryClient();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    return useMutation({
        mutationFn: (
            variables: DeleteSecretMutationVariables & {
                usages: CredentialUsageReference[];
            }
        ) =>
            secretsApi.disconnectCredentialUsages(variables.secret, {
                usages: variables.usages,
            }),
        onSuccess: (
            _response: DisconnectCredentialUsagesResponse,
            variables: DeleteSecretMutationVariables & {
                usages: CredentialUsageReference[];
            }
        ) => {
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'secrets',
                        getMutationSecretQueryKeyPart(variables),
                        'usages',
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating usage queries', err);
                }
            );
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
                        'secrets',
                        'organization',
                        getMutationOrganizationQueryKeyPart(variables),
                    ]),
                }),
                (err) => {
                    log.error('Error invalidating organization secrets queries', err);
                }
            );
        },
    });
}

/**
 * Type guard to check if data is CreateSecretRequest
 */
function isCreateSecretRequest(
    data: CreateSecretRequest | UpdateSecretRequest
): data is CreateSecretRequest {
    return 'organization_uuid' in data && 'system_id' in data;
}

/**
 * Combined hook for saving (create or update) a secret
 */
export function useSaveSecret(secret: SecretIdentityInput) {
    const createMutation = useCreateSecret();
    const updateMutation = useUpdateSecret(secret);

    const isCreating = secret === undefined;

    return {
        mutate: (data: CreateSecretRequest | UpdateSecretRequest) => {
            if (isCreating) {
                if (isCreateSecretRequest(data)) {
                    createMutation.mutate(data);
                } else {
                    throw new Error(
                        'Invalid data for creating secret: missing organization_uuid or system_id'
                    );
                }
            } else {
                if (!isCreateSecretRequest(data)) {
                    updateMutation.mutate(data);
                } else {
                    throw new Error(
                        'Invalid data for updating secret: should not include organization_uuid or system_id'
                    );
                }
            }
        },
        isPending: createMutation.isPending || updateMutation.isPending,
        isSuccess: createMutation.isSuccess || updateMutation.isSuccess,
        isError: createMutation.isError || updateMutation.isError,
        error: createMutation.error || updateMutation.error,
        data: createMutation.data ?? updateMutation.data,
        reset: () => {
            createMutation.reset();
            updateMutation.reset();
        },
    };
}
